import { useEffect, useRef } from "react";
import { Analytics } from "@/lib/analytics";

// Global set to track which sections have been viewed in this session
const viewedSections = new Set<string>();

/**
 * Hook to track when a section becomes visible in the viewport.
 * Fires Analytics.sectionViewed() once per section per session.
 * 
 * @param sectionName - Unique name for the section (e.g., "hero", "pricing", "faq")
 * @param options - IntersectionObserver options
 * @returns ref to attach to the section container
 */
export const useSectionTracking = (
  sectionName: string,
  options: IntersectionObserverInit = { threshold: 0.3 }
) => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    // Skip if already tracked in this session
    if (viewedSections.has(sectionName)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !viewedSections.has(sectionName)) {
            viewedSections.add(sectionName);
            Analytics.sectionViewed(sectionName);
            // Disconnect after firing once
            observer.disconnect();
          }
        });
      },
      options
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [sectionName, options]);

  return sectionRef;
};

/**
 * Reset viewed sections - useful for testing or when user logs out
 */
export const resetViewedSections = () => {
  viewedSections.clear();
};
